import { useState, useEffect } from 'react';

/**
 * Hook personnalisé pour gérer les avances et retenues des employés
 * Ces données sont mensuelles et non permanentes
 */
export const useAvancesRetenuesData = (mois, annee) => {
  const [avancesRetenuesData, setAvancesRetenuesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mois et année par défaut (mois courant)
  const currentDate = new Date();
  const moisCourant = mois || currentDate.getMonth() + 1; // getMonth() retourne 0-11
  const anneeCourante = annee || currentDate.getFullYear();

  useEffect(() => {
    const fetchAvancesRetenues = async () => {
      try {
        setLoading(true);
        // Dans un environnement réel, on ferait un appel API ici
        // const response = await fetch(`/api/avances-retenues?mois=${moisCourant}&annee=${anneeCourante}`);
        // const data = await response.json();
        // setAvancesRetenuesData(data);
        
        // Pour l'instant, on utilise des données mockées
        setAvancesRetenuesData(generateMockAvancesRetenuesData(moisCourant, anneeCourante));
        setLoading(false);
      } catch (err) {
        setError(err);
        setLoading(false);
      }
    };

    fetchAvancesRetenues();
  }, [moisCourant, anneeCourante]);

  /**
   * Génère des données mock pour les avances et retenues
   */
  const generateMockAvancesRetenuesData = (mois, annee) => {
    // Format de date pour la clé (YYYY-MM)
    const periodeKey = `${annee}-${mois.toString().padStart(2, '0')}`;
    
    return [
      {
        id: 1,
        employeeId: 1, // Karim Benali
        periode: periodeKey,
        avanceSalaire: mois % 2 === 0 ? 15000 : 0, // Avance un mois sur deux
        retenueAcompte: mois % 3 === 0 ? 5000 : 0, // Retenue tous les 3 mois
        retenuePret: 3000, // Retenue mensuelle fixe pour un prêt
        joursAbsenceNonJustifiee: mois % 4 === 0 ? 1 : 0, // Absence occasionnelle
        joursAbsenceMaladieNonIndemnisee: mois % 5 === 0 ? 2 : 0,
        retenueOppositionSalaire: 2500,
        retenuePensionAlimentaire: 4000,
        retenueTropPercu: mois === 1 ? 1200 : 0, // Trop-perçu uniquement en janvier
        dateCreation: new Date(),
        creePar: 'system'
      },
      {
        id: 2,
        employeeId: 2, // Fatima Zahra
        periode: periodeKey,
        avanceSalaire: mois % 3 === 0 ? 8000 : 0, // Avance tous les 3 mois
        retenueAcompte: mois % 2 === 0 ? 2500 : 0,
        retenuePret: 1500,
        joursAbsenceNonJustifiee: 0,
        joursAbsenceMaladieNonIndemnisee: mois === 2 ? 1 : 0, // Absence en février
        retenueOppositionSalaire: 0,
        retenuePensionAlimentaire: 0,
        retenueTropPercu: mois === 3 ? 800 : 0, // Trop-perçu uniquement en mars
        dateCreation: new Date(),
        creePar: 'system'
      },
      {
        id: 3,
        employeeId: 3, // Ahmed Rahmani
        periode: periodeKey,
        avanceSalaire: 0,
        retenueAcompte: 0,
        retenuePret: 7500, // Remboursement mensuel d'un prêt important
        joursAbsenceNonJustifiee: mois % 6 === 0 ? 1 : 0,
        joursAbsenceMaladieNonIndemnisee: 0,
        retenueOppositionSalaire: 0,
        retenuePensionAlimentaire: 0,
        retenueTropPercu: 0,
        dateCreation: new Date(),
        creePar: 'system'
      },
      {
        id: 4,
        employeeId: 4, // Amina Belkacem
        periode: periodeKey,
        avanceSalaire: 0,
        retenueAcompte: 0,
        retenuePret: 0,
        joursAbsenceNonJustifiee: 0,
        joursAbsenceMaladieNonIndemnisee: 0,
        retenueOppositionSalaire: 0,
        retenuePensionAlimentaire: 0,
        retenueTropPercu: 0,
        dateCreation: new Date(),
        creePar: 'system'
      }
    ];
  };

  /**
   * Ajoute ou met à jour une avance/retenue pour un employé
   */
  const updateAvanceRetenue = (employeeId, newData) => {
    setAvancesRetenuesData(prevData => {
      const index = prevData.findIndex(item => item.employeeId === employeeId);
      if (index !== -1) {
        // Mettre à jour une entrée existante
        const updatedData = [...prevData];
        updatedData[index] = { ...updatedData[index], ...newData };
        return updatedData;
      } else {
        // Ajouter une nouvelle entrée
        return [...prevData, {
          id: prevData.length + 1,
          employeeId,
          periode: `${anneeCourante}-${moisCourant.toString().padStart(2, '0')}`,
          ...newData,
          dateCreation: new Date(),
          creePar: 'system'
        }];
      }
    });
  };

  /**
   * Récupère les avances/retenues pour un employé spécifique
   */
  const getAvanceRetenueByEmployeeId = (employeeId) => {
    return avancesRetenuesData.find(item => item.employeeId === employeeId) || {
      employeeId,
      avanceSalaire: 0,
      retenueAcompte: 0,
      retenuePret: 0,
      joursAbsenceNonJustifiee: 0,
      joursAbsenceMaladieNonIndemnisee: 0,
      retenueOppositionSalaire: 0,
      retenuePensionAlimentaire: 0,
      retenueTropPercu: 0
    };
  };

  return {
    avancesRetenuesData,
    loading,
    error,
    updateAvanceRetenue,
    getAvanceRetenueByEmployeeId
  };
};

export default useAvancesRetenuesData; 
